// The YouTube player above a video's transcript (a MediaPlayer, see media.ts).
//
// YouTube's IFrame API, loaded on first use: it is the only way to seek the
// embedded player and ask where it is, which is what lets the transcript and
// the video follow each other. The embed is from youtube-nocookie.com, so a
// video isn't tied to the viewer's YouTube account until it is played.
import type { MediaCallbacks, MediaPlayer } from './media.ts';

interface YTPlayer {
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  playVideo(): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  destroy(): void;
}

interface YTNamespace {
  Player: new (element: HTMLElement | string, options: {
    videoId: string;
    host?: string;
    playerVars?: Record<string, string | number>;
    events?: {
      onReady?: () => void;
      onStateChange?: (event: { data: number }) => void;
    };
  }) => YTPlayer;
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const PLAYING = 1;
const BUFFERING = 3;

let apiReady: Promise<YTNamespace> | null = null;

function loadApi(): Promise<YTNamespace> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  apiReady ??= new Promise<YTNamespace>((resolve, reject) => {
    window.onYouTubeIframeAPIReady = () => resolve(window.YT!);
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.onerror = () => {
      apiReady = null;
      reject(new Error('The YouTube player could not be loaded'));
    };
    document.head.append(script);
  });
  return apiReady;
}

let player: YTPlayer | null = null;
let ready = false;
/** Whether the video has been played since it opened: until then, the place in
 *  the transcript is the place in the video, not the player's 0:00. */
let started = false;

/**
 * Put a video's player into `frame`, cued at `start` seconds. Resolves once it
 * can be controlled; a player that fails to load leaves the transcript usable.
 */
export async function openVideo(frame: HTMLElement, videoId: string, start: number, callbacks: MediaCallbacks = {}): Promise<MediaPlayer> {
  closeVideo();
  const controls: MediaPlayer = { seek: seekVideo, time: videoTime, duration: videoDuration, started: videoStarted, close: closeVideo };
  const YT = await loadApi();
  // Somewhere else by the time the script arrived.
  if (!frame.isConnected) return controls;
  const target = document.createElement('div');
  frame.replaceChildren(target);

  await new Promise<void>((resolve) => {
    player = new YT.Player(target, {
      videoId,
      host: 'https://www.youtube-nocookie.com',
      playerVars: { start: Math.floor(start), playsinline: 1, rel: 0 },
      events: {
        onReady: () => {
          ready = true;
          resolve();
        },
        onStateChange: ({ data }) => {
          const playing = data === PLAYING || data === BUFFERING;
          if (playing) started = true;
          callbacks.onPlayingChange?.(playing);
        },
      },
    });
  });
  return controls;
}

function closeVideo(): void {
  player?.destroy();
  player = null;
  ready = false;
  started = false;
}

/** Jump to a moment and play from there. */
function seekVideo(seconds: number): void {
  if (!player || !ready) return;
  player.seekTo(seconds, true);
  player.playVideo();
}

function videoStarted(): boolean {
  return started;
}

/** Where the player is, in seconds; null before it's ready. */
function videoTime(): number | null {
  return player && ready ? player.getCurrentTime() : null;
}

/** The video's length in seconds, once the player knows it. */
function videoDuration(): number | null {
  const duration = player && ready ? player.getDuration() : 0;
  return duration > 0 ? duration : null;
}
